import { Check, ChevronDown, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface DropdownSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface DropdownSelectProps {
  label?: string;
  value?: string;
  values?: string[];
  options: DropdownSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  helperText?: string;
  customDisplayLabel?: string;
  multiple?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  hasMoreOptions?: boolean;
  isLoadingOptions?: boolean;
  onLoadMoreOptions?: () => void;
  emptyStateLabel?: string;
  onChange?: (value: string) => void;
  onMultiChange?: (values: string[]) => void;
  /**
   * Where the option menu opens relative to the trigger. 'auto' (default)
   * flips upward only when there isn't enough room below. 'up' always opens
   * upward — use for triggers inside a list that grows downward, where
   * "space below" is not a reliable signal of visibility.
   */
  menuPlacement?: 'auto' | 'up' | 'down';
  createConfig?: {
    inputLabel?: string;
    inputPlaceholder?: string;
    value: string;
    onValueChange: (value: string) => void;
    onUseValue: (value: string) => void;
    actionLabel?: string;
  };
}

export const DropdownSelect: React.FC<DropdownSelectProps> = ({
  label,
  value,
  values,
  options,
  placeholder = 'Select an option',
  disabled = false,
  helperText,
  customDisplayLabel,
  multiple = false,
  searchable = false,
  searchPlaceholder = 'Search…',
  onSearchChange,
  hasMoreOptions = false,
  isLoadingOptions = false,
  onLoadMoreOptions,
  emptyStateLabel = 'No options available.',
  onChange,
  onMultiChange,
  menuPlacement = 'auto',
  createConfig,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setMenuStyle(null);
      return undefined;
    }

    const MENU_VERTICAL_MARGIN = 8;
    const MENU_MIN_HEIGHT = 160;

    const updateMenuPosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow =
        window.innerHeight - rect.bottom - MENU_VERTICAL_MARGIN;
      const spaceAbove = rect.top - MENU_VERTICAL_MARGIN;
      // Flip the menu above the trigger when there isn't enough room below
      // it but there is more room above — otherwise the panel renders
      // partly (or fully) off-screen with no way to reach later options.
      // menuPlacement 'up'/'down' bypass this heuristic entirely, for
      // triggers where "space below" isn't a reliable visibility signal
      // (e.g. a row inside a list that keeps growing downward).
      const shouldOpenUpward =
        menuPlacement === 'up'
          ? true
          : menuPlacement === 'down'
            ? false
            : spaceBelow < MENU_MIN_HEIGHT && spaceAbove > spaceBelow;

      setMenuStyle({
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(
          shouldOpenUpward ? spaceAbove : spaceBelow,
          MENU_MIN_HEIGHT,
        ),
        ...(shouldOpenUpward
          ? { bottom: window.innerHeight - rect.top + MENU_VERTICAL_MARGIN }
          : { top: rect.bottom + MENU_VERTICAL_MARGIN }),
      });
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, menuPlacement]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        containerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen && searchQuery) {
      setSearchQuery('');
      onSearchChange?.('');
    }
  }, [isOpen, onSearchChange, searchQuery]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );
  const selectedValues = useMemo(() => values ?? [], [values]);
  const selectedValueSet = useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  );

  const filteredOptions = useMemo(() => {
    if (!searchable || onSearchChange || !searchQuery.trim()) {
      return options;
    }

    const query = searchQuery.trim().toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [onSearchChange, options, searchQuery, searchable]);

  const multiDisplayLabel = useMemo(() => {
    if (!multiple || !selectedValues.length) {
      return null;
    }

    const selectedLabels = selectedValues
      .map(
        (selectedValue) =>
          options.find((option) => option.value === selectedValue)?.label,
      )
      .filter(Boolean);

    if (selectedLabels.length === 1) {
      return selectedLabels[0];
    }

    return `${selectedValues.length} selected`;
  }, [multiple, options, selectedValues]);

  const displayLabel =
    customDisplayLabel ||
    multiDisplayLabel ||
    selectedOption?.label ||
    placeholder;
  const hasSelection = multiple
    ? selectedValues.length > 0
    : Boolean(selectedOption || customDisplayLabel);

  return (
    <div ref={containerRef} className="relative w-full">
      {label ? (
        <p className="mb-1 text-xs font-medium text-gray-700">{label}</p>
      ) : null}

      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-center justify-between rounded-xl border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-pos-primary/30 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
      >
        <span className={hasSelection ? '' : 'text-gray-400'}>
          {displayLabel}
        </span>
        <ChevronDown size={16} className="text-gray-400" />
      </button>

      {helperText ? (
        <p className="mt-1 text-xs text-gray-500">{helperText}</p>
      ) : null}

      {isOpen && !disabled && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              style={{
                ...menuStyle,
                position: 'fixed',
                zIndex: 1000,
              }}
              className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
            >
              {searchable ? (
                <div className="border-b border-gray-100 p-3">
                  <input
                    value={searchQuery}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setSearchQuery(nextValue);
                      onSearchChange?.(nextValue);
                    }}
                    placeholder={searchPlaceholder}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                  />
                </div>
              ) : null}

              {createConfig ? (
                <div className="border-b border-gray-100 p-3">
                  {createConfig.inputLabel ? (
                    <p className="mb-2 text-xs font-medium text-gray-700">
                      {createConfig.inputLabel}
                    </p>
                  ) : null}
                  <input
                    value={createConfig.value}
                    onChange={(event) =>
                      createConfig.onValueChange(event.target.value)
                    }
                    placeholder={createConfig.inputPlaceholder}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                  />
                  {createConfig.value.trim() ? (
                    <button
                      type="button"
                      onClick={() => {
                        createConfig.onUseValue(createConfig.value.trim());
                        setIsOpen(false);
                      }}
                      className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-pos-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-pos-primary/90"
                    >
                      <Plus size={14} />
                      {createConfig.actionLabel ||
                        `Use "${createConfig.value.trim()}"`}
                    </button>
                  ) : null}
                </div>
              ) : null}

              {multiple && selectedValues.length ? (
                <div className="border-b border-gray-100 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => onMultiChange?.([])}
                    className="text-xs font-medium text-pos-primary transition-colors hover:text-pos-secondary"
                  >
                    Clear selections
                  </button>
                </div>
              ) : null}

              <div className="min-h-0 max-h-[300px] flex-1 overflow-y-auto py-1">
                {filteredOptions.length ? (
                  filteredOptions.map((option) => {
                    const isSelected = multiple
                      ? selectedValueSet.has(option.value)
                      : option.value === value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        disabled={option.disabled}
                        onClick={() => {
                          if (option.disabled) return;

                          if (multiple) {
                            const nextValues = isSelected
                              ? selectedValues.filter(
                                  (selectedValue) =>
                                    selectedValue !== option.value,
                                )
                              : [...selectedValues, option.value];
                            onMultiChange?.(nextValues);
                            return;
                          }

                          onChange?.(option.value);
                          setIsOpen(false);
                        }}
                        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                          option.disabled
                            ? 'cursor-not-allowed bg-red-50 text-red-400'
                            : isSelected
                              ? 'bg-pos-primary/10 text-pos-primary'
                              : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span>{option.label}</span>
                        {multiple ? (
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                              isSelected
                                ? 'border-pos-primary bg-pos-primary text-white'
                                : 'border-gray-300 bg-white'
                            }`}
                          >
                            {isSelected ? <Check size={12} /> : null}
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-3 text-sm text-gray-500">
                    {emptyStateLabel}
                  </div>
                )}

                {onLoadMoreOptions ? (
                  <div className="border-t border-gray-100 p-2">
                    <button
                      type="button"
                      onClick={onLoadMoreOptions}
                      disabled={!hasMoreOptions || isLoadingOptions}
                      className="w-full rounded-xl px-3 py-2 text-sm font-medium text-pos-primary transition-colors hover:bg-pos-primary/5 disabled:cursor-not-allowed disabled:text-gray-400"
                    >
                      {isLoadingOptions
                        ? 'Loading…'
                        : hasMoreOptions
                          ? 'Load more'
                          : 'All items loaded'}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};
